import { atom, useAtom } from 'jotai';
import type { WritableAtom } from 'jotai';
import { selectAtom } from 'jotai/utils';
import * as Sentry from '@sentry/nextjs';
import { queryParamsAtom } from '@/atoms/queryParamsAtom';

/**
 * Options for useQueryState hook
 */
interface UseQueryStateOptions<T> {
  defaultValue?: T;
  parse?: (value: string) => T;
  serialize?: (value: T) => string;
}

/**
 * Parse a query parameter value to string, handling arrays and undefined
 */
const parseQueryValue = (
  value: string | string[] | undefined
): string | null => {
  if (typeof value === 'string') {
    return value;
  }
  if (Array.isArray(value) && value.length > 0) {
    return value[0];
  }
  return null;
};

/**
 * Cache for query state atoms to ensure singleton behavior
 * Atoms are lightweight and reused across components with the same key.
 *
 * In practice, applications rarely have more than 20-30 unique query parameter keys,
 * so memory usage is negligible. The benefit of caching (avoiding recreation and
 * maintaining state consistency) outweighs the minimal memory cost.
 */
const queryStateAtoms = new Map<
  string,
  WritableAtom<unknown, [unknown], void>
>();

/**
 * Create a query state atom for a specific parameter
 * This atom is optimized to only trigger re-renders when the specific parameter changes
 * using selectAtom for fine-grained subscriptions
 */
const createQueryStateAtom = <T>(
  key: string,
  options: UseQueryStateOptions<T> = {}
) => {
  const { defaultValue, parse, serialize } = options;

  // Create a select atom that only watches this specific query parameter
  const selectQueryParam = selectAtom(
    queryParamsAtom,
    (query) => parseQueryValue(query[key]),
    // Custom equality function to prevent unnecessary re-renders
    (a, b) => a === b
  );

  return atom(
    // Read only the specific parameter - this will only re-render when this parameter changes
    (get) => {
      const rawValue = get(selectQueryParam);

      if (rawValue === null) {
        return defaultValue as T;
      }

      if (parse) {
        try {
          return parse(rawValue);
        } catch {
          return defaultValue as T;
        }
      }

      return rawValue as T;
    },
    // Write to query params
    (get, set, update: T | ((prev: T) => T)) => {
      const currentQuery = get(queryParamsAtom);

      // Get current value efficiently
      const currentRawValue = get(selectQueryParam);
      let currentValue = defaultValue as T;

      if (currentRawValue !== null) {
        if (parse) {
          try {
            currentValue = parse(currentRawValue);
          } catch {
            currentValue = defaultValue as T;
          }
        } else {
          currentValue = currentRawValue as T;
        }
      }

      // Handle functional updates
      const newValue =
        typeof update === 'function'
          ? (update as (prev: T) => T)(currentValue)
          : update;

      const newQuery = { ...currentQuery };

      if (
        newValue === defaultValue ||
        newValue === null ||
        newValue === undefined
      ) {
        // Remove parameter when setting to default/null/undefined
        delete newQuery[key];
      } else {
        // Serialize the value
        const serializedValue = serialize
          ? serialize(newValue)
          : String(newValue);
        newQuery[key] = serializedValue;
      }

      set(queryParamsAtom, newQuery);
    }
  );
};

/**
 * Hook for managing query parameter state, similar to nuqs useQueryState
 *
 * PERFORMANCE OPTIMIZED: Uses selectAtom to ensure components only re-render
 * when their specific query parameter changes, not when any query parameter changes.
 *
 * @param key - The query parameter key
 * @param options - Configuration options
 * @returns [value, setValue] tuple similar to useState
 *
 * @example
 * const [hello, setHello] = useQueryState("hello", { defaultValue: "" });
 * const [count, setCount] = useQueryState("count", {
 *   defaultValue: 0,
 *   parse: (value) => parseInt(value, 10),
 *   serialize: (value) => value.toString()
 * });
 */
export function useQueryState<T = string>(
  key: string,
  options: UseQueryStateOptions<T> = {}
): [T, (value: T | ((prev: T) => T)) => void] {
  // Get or create the atom for this key
  let queryStateAtom = queryStateAtoms.get(key) as
    | WritableAtom<T, [T | ((prev: T) => T)], void>
    | undefined;

  if (!queryStateAtom) {
    queryStateAtom = createQueryStateAtom<T>(key, options);
    queryStateAtoms.set(
      key,
      queryStateAtom as WritableAtom<unknown, [unknown], void>
    );

    // Monitor map growth and alert if it exceeds expected usage
    if (queryStateAtoms.size > 100) {
      Sentry.captureMessage(
        `Query state atoms map has grown to ${queryStateAtoms.size} entries. This may indicate a memory leak.`,
        {
          level: 'warning',
          tags: {
            component: 'useQueryState',
            mapSize: queryStateAtoms.size.toString(),
          },
          extra: {
            currentKeys: Array.from(queryStateAtoms.keys()),
          },
        }
      );
    }
  }

  return useAtom(queryStateAtom);
}

/**
 * Specialized hook for boolean query parameters
 */
export function useQueryStateBoolean(
  key: string,
  options: Omit<UseQueryStateOptions<boolean>, 'parse' | 'serialize'> = {}
): [boolean, (value: boolean | ((prev: boolean) => boolean)) => void] {
  return useQueryState(key, {
    ...options,
    defaultValue: options.defaultValue ?? false,
    parse: (value: string) => value === 'true',
    serialize: (value: boolean) => value.toString(),
  });
}
